import type { FitnessInstructorId } from "@/lib/fitnessInstructor";
import React, { useEffect, useId, useRef, useState } from 'react';
import { useLanguage } from '@/i18n/LanguageProvider';
import { localizeFitnessProductExerciseName } from '@/lib/fitnessProductExerciseLabels';
import ExerciseDemonstration from '@/components/fitness/ExerciseDemonstration';
import ExerciseCoach from '@/components/fitness/ExerciseCoach';
import {
  createFitnessIntervalClock, createFitnessIntervalPlan, FITNESS_INTERVAL_LIMITS,
  pauseFitnessIntervalClock, readFitnessIntervalClock, resolveFitnessIntervalPosition, startFitnessIntervalClock,
  type FitnessIntervalInput, type FitnessIntervalKind, type FitnessIntervalMovementId, type FitnessIntervalPlan,
} from '@/lib/fitnessIntervalProtocol';

interface FitnessIntervalTrainingProps {
  kind: FitnessIntervalKind;
  instructorId?: FitnessInstructorId;
  scopeId: string;
  currentSet: number;
  targetSeconds: number;
  paused: boolean;
  onTargetApply?: (seconds: number) => boolean;
}
const activities: readonly { id: FitnessIntervalMovementId; name: string; labelKey: string }[] = [
  {
    "id": "walking",
    "name": "Walking",
    "labelKey": "fitness.exercises.cardio.caminhada"
  },
  {
    "id": "running",
    "name": "Running",
    "labelKey": "fitness.exercises.cardio.corrida"
  },
  {
    "id": "treadmill",
    "name": "Treadmill",
    "labelKey": "fitness.exercises.cardio.esteira"
  },
  {
    "id": "stationary-bike",
    "name": "Stationary Bike",
    "labelKey": "fitness.exercises.cardio.bike"
  },
  {
    "id": "elliptical",
    "name": "Elliptical",
    "labelKey": "fitness.exercises.cardio.eliptico"
  },
  {
    "id": "rowing-machine",
    "name": "Rowing Machine",
    "labelKey": "fitness.exercises.cardio.remo"
  },
  {
    "id": "jumping-jack",
    "name": "Jumping Jacks",
    "labelKey": "fitness.exercise.jumpingJacks"
  },
  {
    "id": "mountain-climber",
    "name": "Mountain Climber",
    "labelKey": "fitness.exercises.core.mountain_climber"
  },
  {
    "id": "burpee",
    "name": "Burpee",
    "labelKey": "fitness.exercises.cardio.burpee"
  },
  {
    "id": "jump-rope",
    "name": "Jump Rope",
    "labelKey": "fitness.exercises.cardio.pular_corda"
  }
];

const timeLabel = (ms: number) => {
  const seconds = Math.max(0, Math.ceil(ms / 1000)), hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds / 60);
  return (hours ? String(hours) + ':' : '') + String(hours ? minutes % 60 : minutes).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0');
};

function IntervalSession({ kind, instructorId, scopeId, currentSet, targetSeconds, paused, onTargetApply }: FitnessIntervalTrainingProps) {
  const { t, tf, lang } = useLanguage(), formId = useId();
  const [form, setForm] = useState({ effort: '', recovery: '', preparation: '', rounds: '', effortSeconds: '', recoverySeconds: '', warmupSeconds: '', cooldownSeconds: '' });
  const [plan, setPlan] = useState<FitnessIntervalPlan | null>(null);
  const [editing, setEditing] = useState(true), [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0), [running, setRunning] = useState(false), [clockRevision, setClockRevision] = useState(0);
  const clock = useRef(createFitnessIntervalClock(scopeId)), wantsToRun = useRef(false);
  const targetMatches = !plan || !onTargetApply || targetSeconds === plan.totalMs / 1000;
  const title = t(kind === 'hiit' ? 'fitness.interval.hiitTitle' : 'fitness.interval.sprintTitle');
  const activity = (id: FitnessIntervalMovementId) => activities.find(value => value.id === id)!;
  const label = (id: FitnessIntervalMovementId) => localizeFitnessProductExerciseName(activity(id).name, lang) ?? t(activity(id).labelKey);
  const setField = (field: keyof typeof form, value: string) => setForm(previous => ({ ...previous, [field]: value }));

  useEffect(() => {
    let live = true, timer: ReturnType<typeof setInterval> | null = null;
    const now = performance.now();
    if (paused || editing || !targetMatches || !plan) {
      clock.current = pauseFitnessIntervalClock(clock.current, now, scopeId) ?? createFitnessIntervalClock(scopeId);
      if (!targetMatches) wantsToRun.current = false;
      setRunning(false);
    } else if (wantsToRun.current) {
      clock.current = startFitnessIntervalClock(clock.current, now, scopeId) ?? createFitnessIntervalClock(scopeId);
    }
    const tick = () => {
      if (!live) return;
      const active = readFitnessIntervalClock(clock.current, performance.now(), scopeId);
      if (active === null) return;
      const value = plan ? Math.min(plan.totalMs, active) : 0;
      setElapsedMs(value);
      if (plan && value >= plan.totalMs) {
        wantsToRun.current = false;
        clock.current = pauseFitnessIntervalClock(clock.current, performance.now(), scopeId) ?? clock.current;
        if (timer !== null) clearInterval(timer);
      }
      setRunning(!paused && !editing && targetMatches && wantsToRun.current && clock.current.startedAtMs !== null);
    };
    tick();
    if (!paused && !editing && targetMatches && plan && wantsToRun.current && clock.current.startedAtMs !== null) timer = setInterval(tick, 250);
    return () => {
      live = false;
      if (timer !== null) clearInterval(timer);
      clock.current = pauseFitnessIntervalClock(clock.current, performance.now(), scopeId) ?? clock.current;
    };
  }, [paused, editing, targetMatches, plan, scopeId, clockRevision]);

  const prepare = (event: React.FormEvent) => {
    event.preventDefault();
    if (paused) return;
    try {
      const effortMovementId = activities.find(activity => activity.id === form.effort)?.id;
      const preparationMovementId = activities.find(activity => activity.id === form.preparation)?.id;
      if (!effortMovementId || !['rest', 'walking'].includes(form.recovery)) throw new Error('Missing explicit modality');
      const input: FitnessIntervalInput = {
        kind, effortMovementId, recoveryMovementId: form.recovery === 'rest' ? 'rest' : 'walking',
        rounds: Number(form.rounds), effortSeconds: Number(form.effortSeconds), recoverySeconds: Number(form.recoverySeconds),
        warmupSeconds: form.warmupSeconds === '' ? 0 : Number(form.warmupSeconds),
        cooldownSeconds: form.cooldownSeconds === '' ? 0 : Number(form.cooldownSeconds),
        ...(preparationMovementId ? { preparationMovementId } : {}),
      };
      const next = createFitnessIntervalPlan(input);
      wantsToRun.current = false; clock.current = createFitnessIntervalClock(scopeId);
      setElapsedMs(0); setRunning(false); setPlan(next); setEditing(false); setError(null);
    } catch { setError('fitness.interval.invalid'); }
  };
  const toggle = () => {
    if (paused || !plan || !targetMatches || elapsedMs >= plan.totalMs) return;
    wantsToRun.current = !running; setClockRevision(previous => previous + 1);
  };
  const position = plan ? resolveFitnessIntervalPosition(plan, elapsedMs) : null;
  const movement = position?.stage?.movementId ?? null;
  const fieldClass = 'min-h-12 w-full rounded-xl border border-border bg-background px-3 py-2 text-base text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40';
  const buttonClass = 'min-h-12 rounded-xl bg-primary px-4 py-3 font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-40';
  const numeric = (field: 'rounds' | 'effortSeconds' | 'recoverySeconds' | 'warmupSeconds' | 'cooldownSeconds', optional = false) => <label key={field} htmlFor={formId + field} className="grid gap-2 text-sm font-medium">
    {t('fitness.interval.' + field)}
    <input id={formId + field} type="number" inputMode="numeric" step={1} min={optional ? 0 : 1}
      max={field === 'rounds' ? FITNESS_INTERVAL_LIMITS.maxRounds : FITNESS_INTERVAL_LIMITS.maxStageSeconds}
      value={form[field]} onChange={event => setField(field, event.target.value)} disabled={paused} required={!optional} className={fieldClass} />
  </label>;

  return <section className="space-y-4 rounded-2xl border border-border bg-card p-4" aria-label={title}>
    <h3 className="font-display text-lg font-semibold text-foreground">{title}</h3>
    {editing ? <form onSubmit={prepare} className="grid gap-4 sm:grid-cols-2">
      <label htmlFor={formId + 'effort'} className="grid gap-2 text-sm font-medium">
        {t('fitness.interval.effortMovement')}
        <select id={formId + 'effort'} value={form.effort} onChange={event => setField('effort', event.target.value)} disabled={paused} required className={fieldClass}>
          <option value="">{t('fitness.interval.choose')}</option>
          {activities.filter(activity => kind === 'hiit' || ['running', 'treadmill'].includes(activity.id)).map(activity => <option key={activity.id} value={activity.id}>{label(activity.id)}</option>)}
        </select>
      </label>
      <label htmlFor={formId + 'recovery'} className="grid gap-2 text-sm font-medium">
        {t('fitness.interval.recoveryMovement')}
        <select id={formId + 'recovery'} value={form.recovery} onChange={event => setField('recovery', event.target.value)} disabled={paused} required className={fieldClass}>
          <option value="">{t('fitness.interval.choose')}</option>
          <option value="rest">{t('fitness.interval.rest')}</option><option value="walking">{label('walking')}</option>
        </select>
      </label>
      {numeric('rounds')}{numeric('effortSeconds')}{numeric('recoverySeconds')}{numeric('warmupSeconds', true)}{numeric('cooldownSeconds', true)}
      <label htmlFor={formId + 'preparation'} className="grid gap-2 text-sm font-medium">
        {t('fitness.interval.preparationMovement')}
        <select id={formId + 'preparation'} value={form.preparation} onChange={event => setField('preparation', event.target.value)} disabled={paused} className={fieldClass}>
          <option value="">{t('fitness.interval.choose')}</option>
          {activities.map(activity => <option key={activity.id} value={activity.id}>{label(activity.id)}</option>)}
        </select>
      </label>
      <button type="submit" disabled={paused} className={buttonClass + ' sm:col-span-2'}>{t('fitness.interval.prepare')}</button>
    </form> : plan && position && <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl bg-background p-3 text-sm" data-testid="interval-plan-summary">
        {[
          ['effortMovement', label(plan.input.effortMovementId)],
          ['recoveryMovement', plan.input.recoveryMovementId === 'rest' ? t('fitness.interval.rest') : label('walking')],
          ['rounds', String(plan.input.rounds)], ['effortSeconds', String(plan.input.effortSeconds)],
          ['recoverySeconds', String(plan.input.recoverySeconds)],
          ...(plan.input.warmupSeconds > 0 ? [['warmupSeconds', String(plan.input.warmupSeconds)]] : []),
          ...(plan.input.cooldownSeconds > 0 ? [['cooldownSeconds', String(plan.input.cooldownSeconds)]] : []),
          ...(plan.input.preparationMovementId && (plan.input.warmupSeconds > 0 || plan.input.cooldownSeconds > 0)
            ? [['preparationMovement', label(plan.input.preparationMovementId)]] : []),
        ].map(([key, value]) => <React.Fragment key={key}>
          <dt className="text-muted-foreground">{t('fitness.interval.' + key)}</dt><dd className="text-right font-medium text-foreground">{value}</dd>
        </React.Fragment>)}
      </dl>
      <p className="text-sm text-muted-foreground">{tf('fitness.interval.total', { seconds: String(plan.totalMs / 1000) })}</p>
      {!targetMatches ? <button type="button" disabled={paused} className={buttonClass} onClick={() => {
        if (paused || !onTargetApply) return;
        setError(onTargetApply(plan.totalMs / 1000) ? null : 'fitness.interval.stale');
      }}>{tf('fitness.interval.applyTarget', { seconds: String(plan.totalMs / 1000) })}</button> : onTargetApply && <p className="text-sm font-medium text-primary">{t('fitness.interval.targetApplied')}</p>}
      <div className="rounded-xl border border-border bg-background p-4 text-center">
        <p aria-live="polite" className="text-base font-semibold text-foreground">
          {position.completed ? t('fitness.interval.completed') : t('fitness.interval.stage.' + position.stage!.kind)}
        </p>
        {position.stage?.round && <p className="mt-1 text-sm text-muted-foreground">{tf('fitness.interval.round', { current: String(position.stage.round), total: String(plan.input.rounds) })}</p>}
        <output role="timer" aria-live="off" className="my-3 block font-display text-5xl font-semibold tabular-nums text-foreground">{timeLabel(position.stageRemainingMs)}</output>
        <progress max={plan.totalMs} value={position.elapsedMs} aria-label={title} className="h-2 w-full accent-primary" />
      </div>
      {movement && <React.Fragment key={JSON.stringify([scopeId, plan.input, position.stageIndex])}>
        <ExerciseDemonstration instructorId={instructorId} exerciseId={activity(movement).name} exerciseName={label(movement)} paused={paused || !running} avatarPlaybackRequested={running} />
        <ExerciseCoach exerciseId={activity(movement).name} exerciseName={label(movement)} currentSet={currentSet}
          speechScopeId={JSON.stringify([scopeId, plan.input, position.stageIndex])} paused={paused || !running} />
      </React.Fragment>}
      {kind === 'sprint-intervals' && <p className="text-xs leading-relaxed text-muted-foreground">{t('fitness.interval.sprintDemo')}</p>}
      <div className="flex flex-wrap gap-3">
        {!position.completed && <button type="button" onClick={toggle} disabled={paused || !targetMatches} className={buttonClass}>
          {t(running ? 'fitness.interval.pause' : elapsedMs > 0 ? 'fitness.interval.resume' : 'fitness.interval.start')}
        </button>}
        <button type="button" disabled={paused} onClick={() => { wantsToRun.current = false; setRunning(false); setEditing(true); setError(null); }}
          className="min-h-12 rounded-xl border border-border px-4 py-3 font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">{t('fitness.interval.reconfigure')}</button>
      </div>
      {position.completed && <p className="text-sm text-muted-foreground">{t('fitness.interval.manualCompletion')}</p>}
    </div>}
    {error && <p role="alert" className="text-sm text-destructive">{t(error)}</p>}
    <p className="text-xs leading-relaxed text-muted-foreground">{t('fitness.interval.sessionOnly')}</p>
  </section>;
}

/** A changed owner/session/exercise/set mounts a fresh clock and cancels the old interval. */
export default function FitnessIntervalTraining(props: FitnessIntervalTrainingProps) {
  return <IntervalSession key={props.scopeId} {...props} />;
}
